import { useQuery } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { Delete, Edit } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogContent,
  Typography,
} from "@mui/material";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import { ReactElement, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import z from "zod";

import { sortContactFields } from "@stw/audience-contact-fields";

import {
  ActBlueWebhookIntegration,
  AllIntegrationTypes,
  DeleteAudienceContactFieldMutation,
  GetAudienceContactFieldsQuery,
  GetAudienceContactFieldsQueryVariables,
  GetAudienceQuery,
  GetAudienceQueryVariables,
  GetSubaccountSubscriberAudienceQuery,
  GetSubaccountSubscriberAudienceQueryVariables,
  IntegrationServiceType,
  PhoneNumberMessageClass,
  UpdateNonOptInAudienceMutation,
  UpdateNonOptInAudienceMutationVariables,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import { InternalLink } from "~frontend/components/Link";
import PageContainer from "~frontend/components/PageContainer";
import { PageHeader } from "~frontend/components/PageHeader";
import Table, { TableBox } from "~frontend/components/Table";
import { RhfTextField } from "~frontend/components/forms/react-hook-form-components";
import {
  DELETE_AUDIENCE_CONTACT_FIELD,
  GET_AUDIENCE,
  GET_AUDIENCE_CONTACT_FIELDS,
  UPDATE_NON_OPT_IN_AUDIENCE,
} from "~frontend/graphql/audience";
import { GET_SUBACCOUNT_SUBSCRIBER_AUDIENCE } from "~frontend/graphql/shortcode";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import { useFeatureFlag } from "~frontend/providers/LoggedInUserProvider/context";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import theme, { palette } from "~frontend/theme";
import { formatShortDateTime } from "~frontend/util/dateFormat";
import { jobInProgress } from "~frontend/util/jobs";
import useMutation from "~frontend/util/useMutation";

import { integrationLabels } from "../integrationList/types";
import { NumberStatusTag } from "../phoneNumberList/PhoneNumberTable";
import { ActBlueIntegrations } from "./ActBlueIntegrations";
import { AudienceExportButton } from "./AudienceExport";
import FixedKeywords from "./FixedKeywords";
import OptInKeywords from "./OptInKeywords";
import { AddOrEditField } from "./Subscribers/AddOrEditField";
import { FieldTypesByDbType } from "./Subscribers/ViewFields";

const tableSx = {
  ".MuiTableCell-root": {
    borderBottom: `1px solid ${palette.lightGray}`,
    py: 1,
  },
};

function ContactFieldsContainer({
  subaccountId,
  audienceId,
}: {
  subaccountId: string;
  audienceId: string;
}): ReactElement {
  const customFieldsAllowed = useFeatureFlag("audienceCustomFields");

  const {
    data: audienceFields,
    loading,
    error,
  } = useQuery<
    GetAudienceContactFieldsQuery,
    GetAudienceContactFieldsQueryVariables
  >(GET_AUDIENCE_CONTACT_FIELDS, {
    variables: {
      input: { audienceId },
    },
  });

  const [deleteContactField] = useMutation<DeleteAudienceContactFieldMutation>(
    DELETE_AUDIENCE_CONTACT_FIELD,
    {
      refetchQueries: [GET_AUDIENCE_CONTACT_FIELDS],
      awaitRefetchQueries: true,
    }
  );

  const [editingField, setEditingField] = useState<
    | GetAudienceContactFieldsQuery["getAudienceContactFields"][number]
    | "NEW"
    | null
  >(null);

  return (
    <FlexBox flexDirection="column">
      {editingField && (
        <Dialog
          open
          onClose={() => setEditingField(null)}
          fullWidth
          maxWidth="sm"
        >
          <DialogContent>
            <AddOrEditField
              audienceId={audienceId}
              field={
                editingField === "NEW"
                  ? "NEW"
                  : {
                      id: editingField.id,
                      name: editingField.name,
                      type: editingField.type,
                      typeParameters: editingField.typeParameters,
                    }
              }
              onClose={() => setEditingField(null)}
              existingFields={audienceFields?.getAudienceContactFields || []}
            />
          </DialogContent>
        </Dialog>
      )}

      <PageHeader>Contact Fields</PageHeader>
      <FlexBox direction="column" gap={3}>
        <Card>
          <CardHeader title="Standard Fields" />
          <CardContent
            sx={{ px: 2, py: 1, backgroundColor: palette.lightestGray }}
          >
            <AsyncWrapper
              data={audienceFields}
              loading={loading}
              errors={error}
            >
              {({ getAudienceContactFields }) => (
                <Table<
                  GetAudienceContactFieldsQuery["getAudienceContactFields"][number]
                >
                  size="small"
                  columns={[
                    {
                      title: "Field Name",
                      key: "label",
                      sx: { width: "180px" },
                      renderValue: field => field.displayName || field.name,
                    },
                    {
                      title: "Field Type",
                      key: "type",
                      renderValue: field =>
                        FieldTypesByDbType[field.type]?.label || field.type,
                      align: "left",
                    },
                  ]}
                  rows={sortContactFields(
                    getAudienceContactFields.filter(field => !field.isEditable)
                  )}
                  containerSx={tableSx}
                  noFooter
                />
              )}
            </AsyncWrapper>
          </CardContent>
        </Card>
        {customFieldsAllowed && (
          <Card
            sx={{ border: 0, backgroundColor: palette.lightestGray }}
            data-testid="custom-fields-card"
          >
            <CardHeader title="Custom Fields" />
            <CardContent sx={{ px: 2, py: 1, backgroundColor: palette.white }}>
              <AsyncWrapper
                data={audienceFields}
                loading={loading}
                errors={error}
              >
                {({ getAudienceContactFields }) => (
                  <Table<
                    GetAudienceContactFieldsQuery["getAudienceContactFields"][number]
                  >
                    rowActions={[
                      {
                        id: "edit-field",
                        type: "button",
                        icon: <Edit />,
                        tooltipLabel: "Edit field",
                        onClick: ({ id }) => {
                          const field = getAudienceContactFields.find(
                            field => field.id === id
                          );
                          if (field && field.isEditable) {
                            setEditingField(field);
                          }
                        },
                      },
                      {
                        id: "delete-field",
                        type: "button",
                        icon: <Delete />,
                        tooltipLabel: "Delete field",
                        confirm: {
                          title: "Delete Custom Field",
                          description:
                            "Are you sure you want to delete this custom field?",
                        },
                        onClick: async ({ id }) => {
                          await deleteContactField({
                            variables: {
                              input: { id, subaccountId: subaccountId },
                            },
                          });
                        },
                      },
                    ]}
                    size="small"
                    columns={[
                      {
                        title: "Field Name",
                        key: "name",
                        sx: { width: "180px" },
                        renderValue: field => field.name,
                      },
                      {
                        title: "Field Type",
                        key: "type",
                        renderValue: field =>
                          FieldTypesByDbType[field.type]?.label || field.type,
                        align: "left",
                      },
                    ]}
                    rows={sortContactFields(
                      getAudienceContactFields.filter(field => field.isEditable)
                    )}
                    noFooter
                    emptyState="No custom fields found."
                    actionSx={{ ".MuiButtonBase-root": { py: "0", px: "6px" } }}
                    containerSx={tableSx}
                  />
                )}
              </AsyncWrapper>
              <FlexBox justifyContent="flex-end" sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  color="secondary"
                  sx={{ fontSize: "10px" }}
                  onClick={() => setEditingField("NEW")}
                >
                  + Add Custom Field
                </Button>
              </FlexBox>
            </CardContent>
          </Card>
        )}
      </FlexBox>
    </FlexBox>
  );
}

export const AudienceNameFormSchema = z.object({
  name: z.string().min(1, { message: "Audience Name cannot be blank" }),
});

export type AudienceNameFormData = z.infer<typeof AudienceNameFormSchema>;

function AudienceNameForm({
  audience,
}: {
  audience: GetAudienceQuery["getAudience"];
}): ReactElement {
  const { enqueueSnackbar } = useSnackbar();
  const initialValues = { name: audience.name || "" };

  const { control, handleSubmit, formState } = useForm<AudienceNameFormData>({
    resolver: zodResolver(AudienceNameFormSchema),
    mode: "onChange",
    values: initialValues,
  });

  const [updateNonOptInAudience] = useMutation<
    UpdateNonOptInAudienceMutation,
    UpdateNonOptInAudienceMutationVariables
  >(UPDATE_NON_OPT_IN_AUDIENCE);

  async function handleNameUpdate(formData: AudienceNameFormData) {
    const { errors } = await updateNonOptInAudience({
      variables: {
        input: {
          audienceId: audience.id,
          name: formData.name,
        },
      },
    });

    if (errors) {
      enqueueSnackbar("Failed to update audience", { variant: "error" });
    } else {
      enqueueSnackbar("Audience updated successfully", { variant: "success" });
    }
  }

  return (
    <Box>
      <PageHeader>Audience Name</PageHeader>
      <form onSubmit={handleSubmit(handleNameUpdate)}>
        <FlexBox alignItems="baseline" gap={2}>
          <RhfTextField
            control={control}
            name="name"
            label="Audience Name"
            fullWidth
          />
          <Button
            type="submit"
            variant="contained"
            disabled={
              !formState.isValid || !formState.isDirty || formState.isSubmitting
            }
          >
            Save Changes
          </Button>
        </FlexBox>
      </form>
    </Box>
  );
}

function determineSenderType(messageClass: PhoneNumberMessageClass) {
  switch (messageClass) {
    case PhoneNumberMessageClass.ShortCode:
      return "Short Code";
    case PhoneNumberMessageClass.TenDlc:
    case PhoneNumberMessageClass.TollFree:
    case PhoneNumberMessageClass.Unregistered:
      return "Long Code";
  }
}

function OptInSenders({
  audience,
}: {
  audience: GetAudienceQuery["getAudience"];
}): ReactElement {
  const { subaccountLink } = useSubaccount()!;

  return (
    <Box>
      <PageHeader>Senders</PageHeader>
      <Typography sx={{ mb: 2 }}>
        These are the senders that are able to message your short code
        subscriber audience. You can import or archive senders on the{" "}
        <InternalLink to={subaccountLink("/phone-numbers")}>
          Senders Page
        </InternalLink>
        .
      </Typography>
      <TableBox>
        {/* TODO: gray out pending/archived senders */}
        <Table
          columns={[
            {
              key: "sender",
              title: "Sender",
              renderValue: sender => {
                return (
                  <FlexBox alignItems="center" gap={1}>
                    <Typography variant="body2">
                      {sender.phoneNumber}
                    </Typography>{" "}
                    {sender.id === audience.defaultSendingNumber?.id ? (
                      <NumberStatusTag
                        backgroundColor={palette.lightOrange}
                        color={palette.navy}
                      >
                        Primary
                      </NumberStatusTag>
                    ) : null}
                  </FlexBox>
                );
              },
            },
            {
              key: "displayName",
              title: "Name",
              renderValue: sender => sender.displayName,
              align: "left",
            },
            {
              key: "type",
              title: "Type",
              renderValue: sender => determineSenderType(sender.messageClass),
              align: "left",
            },
            {
              key: "createdAt",
              title: "Created At",
              renderValue: sender =>
                formatShortDateTime(DateTime.fromMillis(sender.createdAt)),
              align: "left",
            },
          ]}
          rows={audience.ownedPhoneNumbers || []}
          noFooter
        />
      </TableBox>
    </Box>
  );
}

function OptInIntegrations({
  integrations,
}: {
  integrations: AllIntegrationTypes[];
}): ReactElement {
  const { subaccountLink } = useSubaccount()!;
  const optInIntegrations = integrations.filter(i =>
    [
      IntegrationServiceType.ActBlueWebhook,
      IntegrationServiceType.ActionKit,
    ].includes(i.service)
  );
  return (
    <Box>
      <PageHeader>Opt In Integrations</PageHeader>
      <Typography sx={{ mb: 2 }}>
        You can add or update integrations on the{" "}
        <InternalLink to={subaccountLink("/integrations")}>
          Integrations Page
        </InternalLink>
        .
      </Typography>
      <TableBox
        sx={{
          backgroundColor: "inherit",
          border: `1px solid ${palette.midGray}`,
        }}
      >
        <Table
          columns={[
            {
              key: "integration",
              title: "Integration",
              renderValue: integration => (
                <Typography variant="body2">
                  {integration.name}
                  <br /> ({integrationLabels[integration.service]})
                </Typography>
              ),
              sx: {
                maxWidth: theme.spacing(6),
              },
            },
            {
              key: "message",
              title: "Message",
              align: "left",
              renderValue: integration => {
                switch (integration.service) {
                  case IntegrationServiceType.ActBlueWebhook: {
                    // TODO: we should show media if there is any
                    return (integration as ActBlueWebhookIntegration).settings
                      .sendDonationMessage?.script;
                  }
                  default:
                    return "--";
                }
              },
            },
          ]}
          rows={optInIntegrations}
          containerSx={{
            "& .MuiTableCell-root": {
              borderBottom: "0",
            },
            "& .MuiTableRow-root": {
              borderBottom: `1px solid ${palette.lightGray}`,
              "&:last-child": {
                borderBottom: "0",
              },
            },
          }}
          noFooter
        />
      </TableBox>
    </Box>
  );
}

function OptInAudienceSettingsPage({
  audience,
  integrations,
}: {
  audience: GetAudienceQuery["getAudience"];
  integrations: AllIntegrationTypes[];
}): ReactElement {
  const shortCode = (audience.ownedPhoneNumbers || []).find(
    opn => opn.messageClass === PhoneNumberMessageClass.ShortCode
  );

  return (
    <>
      <Box>
        <FlexBox alignItems="center" sx={{ pb: 2 }} gap={1}>
          <Typography variant="h0Bold" component="div">
            Subscribers{" "}
          </Typography>

          <NumberStatusTag
            backgroundColor={palette.lightOrange}
            color={palette.navy}
          >
            <Typography variant="body1Bold">Opt In</Typography>
          </NumberStatusTag>
        </FlexBox>
        <Typography>
          Configure your settings for your opt in subscriber audience. You may
          only have one opt in audience per subaccount.
        </Typography>
      </Box>
      <OptInSenders audience={audience} />

      {shortCode && (
        <>
          <Box>
            <PageHeader>Keywords</PageHeader>
            <Typography paddingBottom={theme.spacing(2)}>
              Fixed short code keywords are specified in your shortcode contract
              and can not be edited. If there is an issue with one of your fixed
              keywords, please reach out to{" "}
              <a href="mailto:support@scaletowin.com">support@scaletowin.com</a>{" "}
              to correct it.
            </Typography>

            <FixedKeywords
              shortcodeConfig={audience.shortCodeConfig!}
              variant="slim"
            />
          </Box>
          <OptInKeywords
            shortcodeId={shortCode.id}
            shortcodePhoneNumber={shortCode.phoneNumber}
            variant="slim"
          />
        </>
      )}

      <OptInIntegrations integrations={integrations} />
    </>
  );
}

export function AudienceSettingsPage(): ReactElement {
  const { subaccount, subaccountLink } = useSubaccount()!;
  const multiAudienceEnabled = useFeatureFlag("enableMultiAudience");

  const { audienceId } = useParams<{
    audienceId: string;
  }>();

  useBreadcrumbs([
    {
      label: multiAudienceEnabled ? "Audiences" : "Subscribers",
      path: subaccountLink("/audiences"),
    },
    {
      label: "Settings",
      path: subaccountLink(`/audiences/${audienceId}/settings`),
    },
  ]);

  const {
    data: getIntegrations,
    loading: integrationsLoading,
    error: integrationsError,
  } = useQuery<
    GetSubaccountSubscriberAudienceQuery,
    GetSubaccountSubscriberAudienceQueryVariables
  >(GET_SUBACCOUNT_SUBSCRIBER_AUDIENCE, {
    variables: {
      input: {
        subaccountId: subaccount.id,
      },
    },
  });

  const {
    data: audienceData,
    loading,
    error,
    startPolling,
    stopPolling,
  } = useQuery<GetAudienceQuery, GetAudienceQueryVariables>(GET_AUDIENCE, {
    variables: {
      input: {
        audienceId: audienceId!,
      },
    },
    skip: !audienceId,
  });

  const exportJobStatus = audienceData?.getAudience?.exportJob?.status;

  useEffect(() => {
    if (exportJobStatus && jobInProgress({ status: exportJobStatus })) {
      startPolling(5 * 1000);
    }

    return () => stopPolling();
  }, [exportJobStatus, startPolling, stopPolling]);

  if (!audienceId) {
    return <Alert severity="error">No audience found.</Alert>;
  }

  const backLink = multiAudienceEnabled
    ? subaccountLink(`/audiences/${audienceId}`)
    : subaccountLink(`/audiences`);

  const backLinkLabel = multiAudienceEnabled ? "Audiences" : "Subscribers";

  return (
    <PageContainer>
      <Box sx={{ mb: 3 }}>
        <InternalLink variant="body3" to={backLink}>
          {`< Back to ${backLinkLabel}`}
        </InternalLink>
      </Box>
      <AsyncWrapper
        data={{ ...audienceData, ...getIntegrations }}
        loading={[loading, integrationsLoading]}
        errors={[error, integrationsError]}
      >
        {({ getAudience: audience, getSubaccount }) => {
          if (!audience) {
            return (
              <Alert severity="warning">
                No short code found for this subaccount.
              </Alert>
            );
          }

          if (audience.id !== audienceId) {
            throw new Error(`Invalid audience id; please contact support.`);
          }

          const shortcode = audience.ownedPhoneNumbers?.find(
            opn => opn.messageClass === "SHORT_CODE"
          );

          const isOptInAudience = !!audience.shortCodeConfig;

          return (
            <FlexBox flexDirection="column" gap={6}>
              {isOptInAudience ? (
                multiAudienceEnabled ? (
                  <OptInAudienceSettingsPage
                    audience={audience}
                    integrations={
                      (getSubaccount?.integrations ||
                        []) as AllIntegrationTypes[]
                    }
                  />
                ) : (
                  <>
                    {shortcode && (
                      <>
                        <FixedKeywords
                          tableBoxSx={{ backgroundColor: palette.lightestGray }}
                          shortcodeConfig={audience.shortCodeConfig!}
                        />
                        <OptInKeywords
                          shortcodeId={shortcode.id}
                          shortcodePhoneNumber={shortcode.phoneNumber}
                        />
                      </>
                    )}

                    <ActBlueIntegrations
                      integrations={
                        (
                          (getSubaccount?.integrations ||
                            []) as AllIntegrationTypes[]
                        ).filter(
                          integration =>
                            integration.service ===
                              IntegrationServiceType.ActBlueWebhook &&
                            (integration as ActBlueWebhookIntegration).settings
                              ?.subscribeDonors
                        ) as ActBlueWebhookIntegration[]
                      }
                    />
                  </>
                )
              ) : (
                <AudienceNameForm audience={audience} />
              )}

              <ContactFieldsContainer
                subaccountId={subaccount.id}
                audienceId={audienceId}
              />
              <FlexBox flexDirection="column">
                <PageHeader>Export Audience</PageHeader>
                <FlexBox justifyContent="space-between" sx={{ width: "100%" }}>
                  <FlexBox direction="column">
                    <Typography variant="h2" component="div">
                      Generate an export of your audience contacts.
                    </Typography>
                    {audience.exportJob?.result ? (
                      <FlexBox gap={1} sx={{ color: palette.darkGray }}>
                        <Typography variant="body1" component="div">
                          Generated{" "}
                          {DateTime.fromMillis(
                            audience.exportJob.createdAt
                          ).toLocaleString(DateTime.DATETIME_MED)}{" "}
                          by {audience.exportJob.args.requestedBy.fullName}
                          .{" "}
                        </Typography>
                        <AudienceExportButton
                          buttonType="REFRESH_LINK"
                          audience={audience}
                        />
                      </FlexBox>
                    ) : null}
                  </FlexBox>

                  <AudienceExportButton audience={audience} />
                </FlexBox>
              </FlexBox>
            </FlexBox>
          );
        }}
      </AsyncWrapper>
    </PageContainer>
  );
}
